import { useMemo } from 'react';
import { useLeads } from './useLeads';
import { useDeals } from './useDeals';
import { differenceInDays, startOfDay, endOfDay, subDays } from 'date-fns';

export interface UnifiedMetrics {
  // Lead counts
  totalLeads: number;
  openLeads: number;
  resolvedLeads: number;
  
  // Deal counts  
  wonCount: number;
  lostCount: number;
  openDealsCount: number;
  
  // Win Rate (resolved conversion rate) = won / (won + lost)
  winRate: number;
  winRateResolved: number; // same as winRate, explicit name
  
  // Lead-to-Won rate = won / total leads
  leadToWonRate: number;
  
  // Revenue metrics
  wonRevenue: number;
  wonRevenueWithValue: number;
  wonDealsMissingValue: number;
  
  // Pipeline metrics
  pipelineValue: number;
  pipelineDealsWithValue: number;
  pipelineDealsMissingValue: number;
  
  // Forecast
  weightedForecast: number;
  
  // Temperature distribution
  hotLeads: number;
  warmLeads: number;
  coldLeads: number;
  
  // Action/health metrics
  stuckDeals: number;
  overdueFollowups: number;
  noNextStep: number;
  needAction: number;
  
  // Period-based (for dashboard)
  wonRevenue7d: number;
  wonRevenue30d: number;
  wonCount7d: number;
  wonCount30d: number;
  lostCount7d: number;
  lostCount30d: number;
  winRate7d: number;
  winRate30d: number;
  
  // Today's resolved
  resolvedToday: number;
  wonToday: number;
  lostToday: number;
}

interface UseUnifiedMetricsOptions {
  staleThresholdDays?: number;
}

export function useUnifiedMetrics(options: UseUnifiedMetricsOptions = {}): {
  metrics: UnifiedMetrics | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { staleThresholdDays = 7 } = options;
  const { data: leads, isLoading: leadsLoading, error: leadsError } = useLeads();
  const { data: deals, isLoading: dealsLoading, error: dealsError } = useDeals();

  const metrics = useMemo((): UnifiedMetrics | null => {
    if (!leads || !deals) return null;

    const now = new Date();
    const today = startOfDay(now);
    const days7Ago = subDays(now, 7);
    const days30Ago = subDays(now, 30);

    // === LEAD COUNTS ===
    const totalLeads = leads.length;
    const openLeads = leads.filter(l => 
      l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost'
    ).length;
    const resolvedLeads = leads.filter(l => 
      l.pipeline_stage === 'won' || l.pipeline_stage === 'lost'
    ).length;

    // === DEAL COUNTS ===
    // Use deal_status (commercial outcome) instead of stage (process)
    // deal_status: 'open' | 'won' | 'lost' (commercial outcome)
    // stage: 'new' | 'qualified' | 'proposal_sent' | 'negotiation' | 'won' | 'lost' (process)
    const wonDeals = deals.filter(d => (d as any).deal_status === 'won' || d.stage === 'won');
    const lostDeals = deals.filter(d => (d as any).deal_status === 'lost' || d.stage === 'lost');
    const openDeals = deals.filter(d => {
      const dealStatus = (d as any).deal_status;
      return (dealStatus === 'open' || !dealStatus) && d.stage !== 'won' && d.stage !== 'lost';
    });

    const wonCount = wonDeals.length;
    const lostCount = lostDeals.length;
    const openDealsCount = openDeals.length;

    // === WIN RATE (Resolved Conversion Rate) ===
    // CORRECT: won / (won + lost) - only closed deals
    const closedTotal = wonCount + lostCount;
    const winRate = closedTotal > 0 ? (wonCount / closedTotal) * 100 : 0;
    const winRateResolved = winRate;

    // === LEAD-TO-WON RATE ===
    // won / total leads (cohort view)
    const leadToWonRate = totalLeads > 0 ? (wonCount / totalLeads) * 100 : 0;

    // === REVENUE METRICS ===
    // Won revenue = sum of won deal values (null treated as 0)
    const wonRevenueWithValue = wonDeals
      .filter(d => d.value !== null && d.value !== undefined)
      .reduce((sum, d) => sum + (d.value || 0), 0);
    const wonDealsMissingValue = wonDeals.filter(d => d.value === null || d.value === undefined).length;
    const wonRevenue = wonRevenueWithValue; // Show what we have

    // === PIPELINE METRICS ===
    // Pipeline value = sum of OPEN deal values (exclude nulls from sum)
    const pipelineDealsWithValue = openDeals.filter(d => d.value !== null && d.value !== undefined);
    const pipelineValue = pipelineDealsWithValue.reduce((sum, d) => sum + (d.value || 0), 0);
    const pipelineDealsMissingValue = openDeals.filter(d => d.value === null || d.value === undefined).length;

    // === WEIGHTED FORECAST ===
    // sum(value * probability) for OPEN deals with value
    const weightedForecast = pipelineDealsWithValue.reduce(
      (sum, d) => sum + ((d.value || 0) * ((d.probability || 50) / 100)),
      0
    );

    // === TEMPERATURE DISTRIBUTION ===
    const hotLeads = leads.filter(l => 
      l.temperature === 'hot' && l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost'
    ).length;
    const warmLeads = leads.filter(l => 
      l.temperature === 'warm' && l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost'
    ).length;
    const coldLeads = leads.filter(l => 
      l.temperature === 'cold' && l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost'
    ).length;

    // === STUCK DEALS ===
    // OPEN deals where (now - last_activity_at) > N days
    const stuckDeals = openDeals.filter(d => {
      if (!d.updated_at) return false;
      return differenceInDays(now, new Date(d.updated_at)) >= staleThresholdDays;
    }).length;

    // Exclude DONE leads (marked complete / not fit) from action metrics — they are not LOST
    const isLeadDone = (l: (typeof leads)[0]) =>
      l.is_completed === true ||
      (l as { status?: string; completed_at?: string | null }).status === 'done' ||
      (l as { completed_at?: string | null }).completed_at != null;

    // === OVERDUE FOLLOW-UPS ===
    const overdueFollowups = leads.filter(l => {
      if (isLeadDone(l)) return false;
      if (l.pipeline_stage === 'won' || l.pipeline_stage === 'lost') return false;
      if (!l.next_action_at) return false;
      return new Date(l.next_action_at) < now;
    }).length;

    // === NO NEXT STEP ===
    const noNextStep = leads.filter(l => {
      if (isLeadDone(l)) return false;
      if (l.pipeline_stage === 'won' || l.pipeline_stage === 'lost') return false;
      return !l.next_action_at;
    }).length;

    // === NEED ACTION ===
    // Overdue OR stuck OR (Proposal/Negotiation without recent activity)
    const needAction = leads.filter(l => {
      if (isLeadDone(l)) return false;
      if (l.pipeline_stage === 'won' || l.pipeline_stage === 'lost') return false;

      // Overdue
      if (l.next_action_at && new Date(l.next_action_at) < now) return true;

      // No next step
      if (!l.next_action_at) return true;
      
      // Stale (proposal/negotiation stages)
      if (
        (l.pipeline_stage === 'proposal' || l.pipeline_stage === 'appointment') &&
        l.last_interaction_at &&
        differenceInDays(now, new Date(l.last_interaction_at)) > staleThresholdDays
      ) {
        return true;
      }
      
      return false;
    }).length;

    // === PERIOD BASED METRICS ===
    const wonDeals7d = wonDeals.filter(d => 
      d.updated_at && new Date(d.updated_at) >= days7Ago
    );
    const wonDeals30d = wonDeals.filter(d => 
      d.updated_at && new Date(d.updated_at) >= days30Ago
    );
    const lostDeals7d = lostDeals.filter(d => 
      d.updated_at && new Date(d.updated_at) >= days7Ago
    );
    const lostDeals30d = lostDeals.filter(d => 
      d.updated_at && new Date(d.updated_at) >= days30Ago
    );

    const wonRevenue7d = wonDeals7d.reduce((sum, d) => sum + (d.value || 0), 0);
    const wonRevenue30d = wonDeals30d.reduce((sum, d) => sum + (d.value || 0), 0);
    const wonCount7d = wonDeals7d.length;
    const wonCount30d = wonDeals30d.length;
    const lostCount7d = lostDeals7d.length;
    const lostCount30d = lostDeals30d.length;

    const closed7d = wonCount7d + lostCount7d;
    const closed30d = wonCount30d + lostCount30d;
    const winRate7d = closed7d > 0 ? (wonCount7d / closed7d) * 100 : 0;
    const winRate30d = closed30d > 0 ? (wonCount30d / closed30d) * 100 : 0;

    // === TODAY'S RESOLVED ===
    const todayEnd = endOfDay(now);
    const wonToday = leads.filter(l => {
      if (l.pipeline_stage !== 'won') return false;
      const updated = new Date(l.updated_at);
      return updated >= today && updated <= todayEnd;
    }).length;
    const lostToday = leads.filter(l => {
      if (l.pipeline_stage !== 'lost') return false;
      const updated = new Date(l.updated_at);
      return updated >= today && updated <= todayEnd;
    }).length;
    const resolvedToday = wonToday + lostToday;

    return {
      totalLeads,
      openLeads,
      resolvedLeads,
      wonCount,
      lostCount,
      openDealsCount,
      winRate,
      winRateResolved,
      leadToWonRate,
      wonRevenue,
      wonRevenueWithValue,
      wonDealsMissingValue,
      pipelineValue,
      pipelineDealsWithValue: pipelineDealsWithValue.length,
      pipelineDealsMissingValue,
      weightedForecast,
      hotLeads,
      warmLeads,
      coldLeads,
      stuckDeals,
      overdueFollowups,
      noNextStep,
      needAction,
      wonRevenue7d,
      wonRevenue30d,
      wonCount7d,
      wonCount30d,
      lostCount7d,
      lostCount30d,
      winRate7d,
      winRate30d,
      resolvedToday,
      wonToday,
      lostToday,
    };
  }, [leads, deals, staleThresholdDays]);

  return {
    metrics,
    isLoading: leadsLoading || dealsLoading,
    error: leadsError || dealsError || null,
  };
}

// Helper function to format currency
export function formatCurrency(value: number, currency: string = 'EUR'): string {
  const symbol = currency === 'EUR' ? '€' : '$';
  if (value >= 1000000) return `${symbol}${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `${symbol}${(value / 1000).toFixed(2)}K`;
  return `${symbol}${value.toFixed(0)}`;
}
